import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
// import { scaleTime } from 'd3-scale';
// import { utcDay } from 'd3-time';
import {
  Paper,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  TextField,
  Chip,
  Stack,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import {
  discontinuousTimeScaleProviderBuilder,
  Chart,
  ChartCanvas,
  CandlestickSeries,
  XAxis,
  YAxis,
  CrossHairCursor,
  MouseCoordinateX,
  MouseCoordinateY,
  OHLCTooltip,
  ema, // Added EMA
  sma, // Added SMA
  LineSeries, // Added LineSeries for drawing indicators
} from 'react-financial-charts';

// Define the new leaner structure for historical rate entries
interface HistoricalRateEntry {
  id?: string;
  recordedAt: string;
  quantityFrom: string | number;
  quantityTo: string | number;
  exchangeRate?: number;
}

// Helper formatting functions
const formatPrice = (num: number) => num.toFixed(4);

interface CandleChartProps {
  historicalRates: HistoricalRateEntry[]; // Updated to use HistoricalRateEntry
  selectedItemName: string;
  selectedCurrencyName: string;
  isLoading: boolean;
  leagueName?: string; // Added leagueName as an optional prop
}

// Interface for the transformed OHLC data
interface OHLCData {
  date: Date;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number; // Optional volume data
}

// Define indicator types and their parameters
type IndicatorType = 'EMA' | 'SMA'; // Add more types like 'BollingerBands'

interface IndicatorParameter {
  name: string;
  label: string;
  defaultValue: number;
  type: 'number'; // Can be extended for other types
}

const INDICATOR_CONFIG: Record<IndicatorType, IndicatorParameter[]> = {
  EMA: [{ name: 'period', label: 'Period', defaultValue: 20, type: 'number' }],
  SMA: [{ name: 'period', label: 'Period', defaultValue: 20, type: 'number' }],
  // BollingerBands: [
  //   { name: 'period', label: 'Period', defaultValue: 20, type: 'number' },
  //   { name: 'stdDev', label: 'Std. Dev.', defaultValue: 2, type: 'number' },
  // ],
};

interface ActiveIndicator {
  id: string; // Unique ID for the indicator instance
  type: IndicatorType;
  params: Record<string, number>;
  yAccessor?: (d: any) => number; // To access the calculated indicator value
  stroke?: string; // Color for the line
}

const CandleChart: React.FC<CandleChartProps> = ({ historicalRates, selectedItemName, selectedCurrencyName, isLoading, leagueName }) => {
  const [candleSize, setCandleSize] = useState<string>('1d'); // Default to 1-day candles
  const [dimensions, setDimensions] = useState({
    width: 800,
    height: 400, // Initialize with a base height
  });
  const [activeIndicators, setActiveIndicators] = useState<ActiveIndicator[]>([]);
  const [selectedIndicatorType, setSelectedIndicatorType] = useState<IndicatorType | ''>('');
  const [indicatorParams, setIndicatorParams] = useState<Record<string, string>>({});

  // Ref for the container to measure its dimensions
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: width,
          height: 400, // Keep a fixed height or adjust if other panel types are added later
        });
      }
    };

    // Initial update
    updateDimensions();

    // Add resize event listener
    window.addEventListener('resize', updateDimensions);

    return () => {
      window.removeEventListener('resize', updateDimensions);
    };
  }, [activeIndicators]); // Add activeIndicators dependency

  // Helper function to safely get the exchange rate value
  const getRateValue = (rate: HistoricalRateEntry): number => {
    if (rate.exchangeRate !== undefined) {
      return typeof rate.exchangeRate === 'string' ? parseFloat(rate.exchangeRate) : rate.exchangeRate;
    }
    const qTo = typeof rate.quantityTo === 'string' ? parseFloat(rate.quantityTo) : rate.quantityTo;
    const qFrom = typeof rate.quantityFrom === 'string' ? parseFloat(rate.quantityFrom) : rate.quantityFrom;

    if (isNaN(qTo) || isNaN(qFrom) || qFrom === 0) {
      // console.warn('Invalid quantity for rate calculation:', rate);
      return 0; // Or handle error appropriately
    }
    return qTo / qFrom;
  };

  // Function to get the appropriate time format key based on candle size
  const getTimeFormatKey = (date: Date, candleSize: string): string => {
    switch (candleSize) {
      case '15m':
        // Format: YYYY-MM-DD HH:MM rounded to nearest 15 minutes
        const minutes = date.getMinutes();
        const roundedMinutes = Math.floor(minutes / 15) * 15;
        const roundedDate = new Date(date);
        roundedDate.setMinutes(roundedMinutes, 0, 0);
        return format(roundedDate, 'yyyy-MM-dd HH:mm');
      case '1h':
        // Format: YYYY-MM-DD HH:00
        return format(date, 'yyyy-MM-dd HH:00');
      case '4h':
        // Format: YYYY-MM-DD HH:00 rounded to 4-hour intervals
        const hours = date.getHours();
        const roundedHours = Math.floor(hours / 4) * 4;
        const roundedHourDate = new Date(date);
        roundedHourDate.setHours(roundedHours, 0, 0, 0);
        return format(roundedHourDate, 'yyyy-MM-dd HH:00');
      case '1d':
        // Format: YYYY-MM-DD
        return format(date, 'yyyy-MM-dd');
      case '1w':
        // Format to the start of the week (Sunday or Monday based on locale)
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay()); // Set to Sunday
        return format(startOfWeek, 'yyyy-MM-dd');
      default:
        // Default to daily
        return format(date, 'yyyy-MM-dd');
    }
  };

  // Transform ItemExchangeRate[] data to OHLC format based on selected candle size
  const ohlcData = useMemo(() => {
    if (historicalRates.length === 0) return [];

    // Enable verbose logging for debugging
    console.log('Transforming historical rates:', historicalRates.length, 'data points');

    // Group rates by selected timeframe to create OHLC candles
    const ratesByTimeframe = new Map<string, HistoricalRateEntry[]>();

    historicalRates.forEach(rate => {
      const date = new Date(rate.recordedAt);
      const timeKey = getTimeFormatKey(date, candleSize);

      if (!ratesByTimeframe.has(timeKey)) {
        ratesByTimeframe.set(timeKey, []);
      }
      ratesByTimeframe.get(timeKey)?.push(rate);
    });

    // Convert grouped data to OHLC format
    const ohlcPoints: OHLCData[] = Array.from(ratesByTimeframe.entries()).map(([timeKey, periodRates]) => {
      // Log the period data for debugging
      console.log(`Processing timeframe ${timeKey} with ${periodRates.length} rates`);
      // Sort rates by timestamp to find open/close
      periodRates.sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());

      // Get exchange rate values using the helper
      const values = periodRates.map(getRateValue);

      const firstRate = periodRates[0];
      const lastRate = periodRates[periodRates.length - 1];

      let periodDate: Date;

      // Create proper date object based on the timeKey and candle size
      switch (candleSize) {
        case '15m':
        case '1h':
        case '4h':
          periodDate = new Date(timeKey);
          break;
        case '1d':
          periodDate = new Date(timeKey);
          break;
        case '1w':
          periodDate = new Date(timeKey);
          break;
        default:
          periodDate = new Date(timeKey);
      }

      return {
        date: periodDate,
        open: getRateValue(firstRate),
        high: Math.max(...values),
        low: Math.min(...values),
        close: getRateValue(lastRate),
        volume: periodRates.length, // Using count as volume
      };
    });

    // Log the final OHLC data for debugging
    console.log('Generated OHLC data:', ohlcPoints.length, 'candles');
    ohlcPoints.forEach((candle, i) => {
      if (i < 5 || i > ohlcPoints.length - 5) {
        // Log first 5 and last 5 candles
        console.log(
          `Candle ${i}: Date=${candle.date.toISOString()}, O=${candle.open}, H=${candle.high}, L=${candle.low}, C=${candle.close}`,
        );
      }
    });

    // Sort by date
    ohlcPoints.sort((a, b) => a.date.getTime() - b.date.getTime());

    return ohlcPoints;
  }, [historicalRates, candleSize]);

  // Calculate indicators and merge them into the data
  const dataWithIndicators = useMemo(() => {
    let processedData = [...ohlcData]; // Start with base OHLC data

    activeIndicators.forEach(indicator => {
      if (indicator.type === 'EMA') {
        const calculator = ema()
          .options({
            windowSize: indicator.params.period,
          })
          .stroke(indicator.stroke) // Apply stroke using the .stroke() method
          .merge((d: any, c: any) => {
            d[`ema${indicator.params.period}`] = c;
          })
          .accessor((d: any) => d[`ema${indicator.params.period}`]);
        processedData = calculator(processedData);
      } else if (indicator.type === 'SMA') {
        const calculator = sma()
          .options({
            windowSize: indicator.params.period,
          })
          .stroke(indicator.stroke) // Apply stroke using the .stroke() method
          .merge((d: any, c: any) => {
            d[`sma${indicator.params.period}`] = c;
          })
          .accessor((d: any) => d[`sma${indicator.params.period}`]);
        processedData = calculator(processedData);
      }
    });
    return processedData;
  }, [ohlcData, activeIndicators]);

  // Time scale provider
  const timeScaleProvider = useMemo(() => {
    const buildTimeScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor((d: OHLCData) => d.date);
    return buildTimeScaleProvider(dataWithIndicators); // Use dataWithIndicators
  }, [dataWithIndicators]); // Use dataWithIndicators

  const { data, xScale, xAccessor, displayXAccessor } = timeScaleProvider;

  // Calculate the visible data range based on candle size
  const xExtents = useMemo(() => {
    if (data.length === 0) return [0, 0];

    // Determine number of candles to display based on timeframe
    let visibleCandles: number;
    switch (candleSize) {
      case '15m':
        visibleCandles = 48;
        break; // 12 hours of data
      case '1h':
        visibleCandles = 24;
        break; // 1 day of data
      case '4h':
        visibleCandles = 30;
        break; // 5 days of data
      case '1d':
        visibleCandles = 30;
        break; // 1 month of data
      case '1w':
        visibleCandles = 12;
        break; // 3 months of data
      default:
        visibleCandles = 30;
    }

    const startIndex = Math.max(0, data.length - visibleCandles);
    const start = xAccessor(data[startIndex]);
    const end = xAccessor(data[data.length - 1]);
    return [start, end];
  }, [xAccessor, data, candleSize]);

  // Handler for candle size changes
  const handleCandleSizeChange = (event: SelectChangeEvent) => {
    setCandleSize(event.target.value);
  };

  const handleIndicatorTypeChange = (event: SelectChangeEvent<IndicatorType | ''>) => {
    const type = event.target.value as IndicatorType | '';
    setSelectedIndicatorType(type);
    if (type && INDICATOR_CONFIG[type]) {
      const defaultParams: Record<string, string> = {};
      INDICATOR_CONFIG[type].forEach(param => {
        defaultParams[param.name] = param.defaultValue.toString();
      });
      setIndicatorParams(defaultParams);
    } else {
      setIndicatorParams({});
    }
  };

  const handleIndicatorParamChange = (paramName: string, value: string) => {
    setIndicatorParams(prev => ({ ...prev, [paramName]: value }));
  };

  const handleAddIndicator = () => {
    if (!selectedIndicatorType) return;

    const paramsAsNumbers: Record<string, number> = {};
    for (const key in indicatorParams) {
      paramsAsNumbers[key] = parseFloat(indicatorParams[key]);
      if (isNaN(paramsAsNumbers[key])) {
        alert(`Invalid value for parameter: ${key}`);
        return;
      }
    }

    const newIndicator: ActiveIndicator = {
      id: `${selectedIndicatorType}_${Date.now()}`, // Simple unique ID
      type: selectedIndicatorType,
      params: paramsAsNumbers,
      // Define yAccessor and stroke based on type and params
      yAccessor: (d: any) => {
        if (selectedIndicatorType === 'EMA') return d[`ema${paramsAsNumbers.period}`];
        if (selectedIndicatorType === 'SMA') return d[`sma${paramsAsNumbers.period}`];
        return undefined;
      },
      stroke: selectedIndicatorType === 'EMA' ? '#ff7f0e' : '#1f77b4', // Example colors
    };

    setActiveIndicators(prev => [...prev, newIndicator]);
    setSelectedIndicatorType(''); // Reset selection
    setIndicatorParams({});
  };

  const handleRemoveIndicator = (idToRemove: string) => {
    setActiveIndicators(prev => prev.filter(ind => ind.id !== idToRemove));
  };

  // Calculate yExtents for the main price chart, considering indicators
  const mainChartYExtents = (d: OHLCData) => {
    let min = d.low;
    let max = d.high;
    activeIndicators.forEach(ind => {
      if (ind.yAccessor && (ind.type === 'EMA' || ind.type === 'SMA')) {
        // Only for overlay indicators
        const value = ind.yAccessor(d);
        if (value !== undefined) {
          min = Math.min(min, value);
          max = Math.max(max, value);
        }
      }
    });
    return [max, min]; // YAxis expects [max, min]
  };

  return (
    <Paper sx={{ p: 2, height: 'auto', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant='h6' sx={{ mb: { xs: 1, md: 0 } }}>
          {selectedItemName} to {selectedCurrencyName} Price History - {leagueName || 'Standard'}
        </Typography>

        <FormControl size='small' sx={{ minWidth: 120, mb: { xs: 1, md: 0 }, mr: { md: 1 } }}>
          <InputLabel id='candle-size-select-label'>Candle Size</InputLabel>
          <Select labelId='candle-size-select-label' value={candleSize} label='Candle Size' onChange={handleCandleSizeChange}>
            <MenuItem value='15m'>15 Minutes</MenuItem>
            <MenuItem value='1h'>1 Hour</MenuItem>
            <MenuItem value='4h'>4 Hours</MenuItem>
            <MenuItem value='1d'>1 Day</MenuItem>
            <MenuItem value='1w'>1 Week</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Indicator Controls */}
      <Box sx={{ mb: 2, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
        <Typography variant='subtitle1' gutterBottom>
          Configure Indicators
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='flex-start'>
          <FormControl size='small' sx={{ minWidth: 150 }}>
            <InputLabel id='indicator-type-select-label'>Indicator Type</InputLabel>
            <Select
              labelId='indicator-type-select-label'
              value={selectedIndicatorType}
              label='Indicator Type'
              onChange={handleIndicatorTypeChange}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              <MenuItem value='EMA'>EMA (Exponential Moving Average)</MenuItem>
              <MenuItem value='SMA'>SMA (Simple Moving Average)</MenuItem>
            </Select>
          </FormControl>

          {selectedIndicatorType &&
            INDICATOR_CONFIG[selectedIndicatorType] &&
            INDICATOR_CONFIG[selectedIndicatorType].map(param => (
              <TextField
                key={param.name}
                size='small'
                label={param.label}
                type={param.type}
                value={indicatorParams[param.name] || ''}
                onChange={e => handleIndicatorParamChange(param.name, e.target.value)}
                sx={{ minWidth: 100 }}
              />
            ))}
          <Button
            variant='contained'
            onClick={handleAddIndicator}
            disabled={!selectedIndicatorType || Object.values(indicatorParams).some(p => p === '')}
          >
            Add Indicator
          </Button>
        </Stack>
        {activeIndicators.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant='subtitle2'>Active Indicators:</Typography>
            <Stack direction='row' spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
              {activeIndicators.map(ind => (
                <Chip
                  key={ind.id}
                  label={`${ind.type} (${Object.entries(ind.params)
                    .map(([k, v]) => `${k}:${v}`)
                    .join(', ')})`}
                  onDelete={() => handleRemoveIndicator(ind.id)}
                  deleteIcon={<DeleteIcon />}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading historical data...</Typography>
        </Box>
      ) : ohlcData.length === 0 ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
          <Typography>No historical data available for this item</Typography>
        </Box>
      ) : (
        <Box ref={containerRef} sx={{ height: 400, width: '100%' }}>
          <ChartCanvas
            width={dimensions.width}
            height={dimensions.height}
            ratio={1} // Adjust based on device pixel ratio if needed
            margin={{ left: 60, right: 60, top: 20, bottom: 40 }} // Increased margins
            seriesName={selectedItemName}
            data={data}
            xScale={xScale}
            xAccessor={xAccessor}
            displayXAccessor={displayXAccessor}
            xExtents={xExtents}
          >
            <Chart id={1} yExtents={mainChartYExtents} padding={{ top: 10, bottom: 20 }}>
              <XAxis axisAt='bottom' orient='bottom' ticks={candleSize === '15m' ? 6 : candleSize === '1h' ? 8 : 10} />
              <YAxis tickFormat={formatPrice} axisAt='right' orient='right' />

              <CandlestickSeries
                widthRatio={0.9} // Increased width ratio for all candlesticks
                wickStroke={d => (d.close > d.open ? '#26a69a' : '#ef5350')}
                fill={d => (d.close > d.open ? '#26a69a' : '#ef5350')}
                // Remove properties that aren't supported by the component
              />

              {/* Render Active Overlay Indicators */}
              {activeIndicators.map(indicator => {
                if (!indicator.yAccessor || (indicator.type !== 'EMA' && indicator.type !== 'SMA')) return null;

                // EMA and SMA are drawn as LineSeries on the main chart
                // Stroke is now defined in the calculator options
                return <LineSeries key={indicator.id} yAccessor={indicator.yAccessor} highlightOnHover />;
              })}

              <MouseCoordinateX
                at='bottom'
                orient='bottom'
                displayFormat={(date: Date) => {
                  // Format depends on candle size
                  switch (candleSize) {
                    case '15m':
                      return format(date, 'MMM dd HH:mm');
                    case '1h':
                    case '4h':
                      return format(date, 'MMM dd HH:00');
                    case '1d':
                      return format(date, 'MMM dd');
                    case '1w':
                      return format(date, 'MMM dd') + ' (Week)';
                    default:
                      return format(date, 'MMM dd HH:mm');
                  }
                }}
              />
              <MouseCoordinateY at='right' orient='right' displayFormat={(value: number) => value.toFixed(4)} />

              <OHLCTooltip origin={[-40, 0]} textFill={d => (d.close > d.open ? '#26a69a' : '#ef5350')} labelFill='#9575cd' />
            </Chart>
            <CrossHairCursor />
          </ChartCanvas>
        </Box>
      )}
    </Paper>
  );
};

export default CandleChart;
